// react, ability, auth and services
import crudsService from "services/cruds-service";
import { useAbility } from "@casl/react";
import { AuthContext } from "context";
import InventoryService from "services/inventory-items-service";
import { AbilityContext } from "Can";
import { useContext, useEffect, useState } from "react";

// Material Dashboard and MUI components
import DoneAllTwoToneIcon from "@mui/icons-material/DoneAllTwoTone";
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone';
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import MDAvatar from "components/MDAvatar";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import MDBox from "components/MDBox";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

//E4A Components
import DataTable from "e4acomponents/Tables/DataTable";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

// Reservations Dialog component
export default function ReservationsDialog({
    open,
    onClose,
    reservations,
    refreshItemData
}) {
    const [reservationsData, setReservationsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [avatars, setAvatars] = useState({});
    const [currentUser, setCurrentUser] = useState(null);

    // Snakcbar states
    const [snackbarProps, setSnackbarProps] = useState(false);

    // Confirmation Dialog states
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({});

    // Confirmation Dialog texts
    const fulfillPrompt = "Are you sure you want to fulfill this reservation?";
    const fulfillTitle = "Fulfill Reservation";
    const cancelPrompt = "Are you sure you want to cancel this reservation?";
    const cancelTitle = "Cancel Reservation";

    const ability = useAbility(AbilityContext);
    const authContext = useContext(AuthContext);

    useEffect(() => {
        const fetchReservations = async () => {
            try {
                setLoading(true);
                
                const images = await fetchImages(reservations);
                setAvatars(images);
                setReservationsData(reservations);

                const user = await authContext.getCurrentUser();
                setCurrentUser(user);

            } catch (error) {
                console.error("Error fetching reservations:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchReservations();
    }, [reservations]);

    useEffect(() => {
        if (reservationsData && reservationsData.length > 0) {
            const rows = getRows(reservationsData);
            setTableData(rows);
        }
    }, [reservationsData]);

    // Fetch user profile images for reservations
    const fetchImages = async (reservations) => {
        let images = {};
        for (let i = 0; i < reservations.length; i++) {
            const rsrv = reservations[i];
            if (!images[rsrv.user._id] && !avatars[rsrv.user._id]) {
                try {
                    images[rsrv.user._id] = await crudsService.getImage(rsrv.user.profile_image);
                } catch (err) {
                    images[rsrv.user._id] = null;
                }
            }
        }
        return images;
    }

    // Status styles
    const statusStyles = {
        allocated: { color: "info", text: "Allocated" },
        fulfilled: { color: "success", text: "Fulfilled" },
        cancelled: { color: "error", text: "Cancelled" },
    }

    // Prepare table rows from reservations data
    const getRows = (info) => {
        return info.map((reservation) => ({
            id: reservation._id,
            qty: reservation.qty,
            createdAt: new Date(reservation.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            }),
            user: reservation.user?.name || 'Unknown',
            userId: reservation.user?._id || null,
            avatar: avatars[reservation.user?._id] || null,
            status: reservation.status,
            comment: reservation.comment || '',
            statusChip: (
                <Chip variant="outlined" size="small"
                    color={statusStyles[reservation.status]?.color || "default"}
                    label={statusStyles[reservation.status]?.text || reservation.status}
                />
            )
        }))
    }

    const dataTableData = {
        columns: [
            {
                Header: "Avatar", accessor: "avatar", disableSortBy: true,
                Cell: ({ cell: { value } }) => {
                    return (
                        <MDBox
                            sx={{ cursor: "pointer" }}
                        >
                            <MDAvatar src={value} alt="profile-image" size="xs" shadow="sm" />
                        </MDBox >
                    )
                }
            },
            { Header: 'User', accessor: 'user' },
            { Header: 'Created At', accessor: 'createdAt' },
            {Header: 'Comment', accessor: 'comment' },
            { Header: 'Quantity', accessor: 'qty' },
            { Header: 'Status', accessor: 'status', Cell: ({ row }) => row.original.statusChip },
            {
                Header: 'Actions', accessor: 'actions', disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        ability.can("manage", "reservation") ||
                        currentUser === row.original.userId
                    ) && (row.original.status === 'allocated') &&
                        (
                            <MDBox>
                                <Tooltip
                                    title="Fulfill Reservation"
                                    onClick={() => handleOpenConfirmDialog(row.original.id, 'fulfilled')}
                                >
                                    <IconButton>
                                        <DoneAllTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title="Cancel Reservation"
                                    onClick={() => handleOpenConfirmDialog(row.original.id, 'cancelled')}
                                >
                                    <IconButton>
                                        <ClearTwoToneIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        )
                }
            }
        ],
        rows: tableData
    }

    // Update local state to reflect status change
    const reflectStatusChange = (reservationId, newStatus) => {
        const updatedReservations = reservationsData.map((reservation) =>
            reservation._id === reservationId
                ? { ...reservation, status: newStatus }
                : reservation
        );
        setReservationsData(updatedReservations);
    }

    // Handle dialog cancel/close
    const handleClose = () => {
        setReservationsData([]);
        onClose();
    };

    // Handle Confirmation Dialog close
    const onCloseConfirmDialog = (response, title, reservationId) => {
        setOpenConfirmDialog(false);
        if (response) {
            if (title === fulfillTitle) {
                handleStatusChange(reservationId, 'fulfilled');
            } else if (title === cancelTitle) {
                handleStatusChange(reservationId, 'cancelled');
            }
        }
    }

    // Handles opening Confirmation Dialog
    const handleOpenConfirmDialog = (reservationId, status) => {
        if (status === 'fulfilled') {
            setDialogProps({
                title: fulfillTitle,
                prompt: fulfillPrompt,
                id: reservationId,
                onClose: onCloseConfirmDialog
            })
        } else if (status === 'cancelled') {
            setDialogProps({
                title: cancelTitle,
                prompt: cancelPrompt,
                id: reservationId,
                onClose: onCloseConfirmDialog
            })
        }
        setOpenConfirmDialog(true);
    }

    // Handle Status Change (Fulfill/Cancel)
    const handleStatusChange = async (reservationId, status) => {
        try {
            const payload = {
                data: {
                    attributes: {
                        status: status
                    }
                }
            };

            await InventoryService.editReservationStatus(reservationId, payload);
            reflectStatusChange(reservationId, status);

            if ((status === 'cancelled' || status === 'fulfilled') && refreshItemData) {
                await refreshItemData();
            }

            // Feedback to user
            setSnackbarProps({
                open: true,
                message: status === 'fulfilled' ? 'Reservation fulfilled successfully.' : 'Reservation cancelled successfully.',
            })

        } catch (error) {
            console.error("Error fulfilling reservation:", error);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
        >
            <ConfirmDialog
                open={openConfirmDialog}
                props={dialogProps}
            />

            <Snackbar
                open={snackbarProps.open}
                autoHideDuration={3000}
                onClose={() => setSnackbarProps({ open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarProps({ open: false })}
                    severity="success"
                    sx={{ width: '100%', alignItems: 'center' }}
                >
                    {snackbarProps.message}
                </Alert>
            </Snackbar>

            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0 }}>
                Item's Reservations
                <Tooltip title="Close">
                    <IconButton size="small" onClick={handleClose}>
                        <ClearTwoToneIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent>
                <MDBox mt={5}>
                    {loading ? (
                        <MDBox py={4} textAlign="center">
                            Loading reservations...
                        </MDBox>
                    ) : tableData.length === 0 ? (
                        <MDBox py={4} textAlign="center">
                            No reservations found.
                        </MDBox>
                    ) : (
                        <DataTable
                            table={dataTableData}
                            isSorted={false}
                            entriesPerPage={false}
                            showTotalEntries={false}
                            noEndBorder
                            noToolbar
                        />
                    )}
                </MDBox>
            </DialogContent>
        </Dialog>
    )
}